import type { AdditionalFormat, TranscriptionResponse } from "@ui/components/demos/STT/types";
import type { ItemBase } from "@ui/components/core/ListBox";

import { useMemo, useState } from "react";

import { base64ToUint8Array } from "@ui/components/demos/STT/utils";

function downloadAdditionalFormat(
  format: AdditionalFormat,
  baseName: string,
  onTranscriptionDownloaded?: (download_format: string) => void,
) {
  const filename = `${baseName}.${format.file_extension}`;
  const rawContent = format.is_base64_encoded ? base64ToUint8Array(format.content) : format.content;
  const blob = new Blob([rawContent as BlobPart], { type: format.content_type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  onTranscriptionDownloaded?.(format.requested_format);
}

export function useSTTFloatingCardDownloads({
  uploadedTranscription,
  uploadFileName,
  sampleName,
  onDownload,
  onTranscriptionDownloaded,
}: {
  uploadedTranscription: TranscriptionResponse | null;
  uploadFileName: string | null;
  sampleName: string;
  onDownload?: () => void;
  onTranscriptionDownloaded?: (download_format: string) => void;
}) {
  const [isFormatDropdownOpen, setIsFormatDropdownOpen] = useState(false);
  const additionalFormats = uploadedTranscription?.additional_formats ?? [];
  const hasFormats = additionalFormats.length > 0;

  const baseName = uploadFileName ? uploadFileName.replace(/\.[^/.]+$/, "") : sampleName;

  const formatItems = useMemo(
    () =>
      additionalFormats.map(format => ({
        id: format.requested_format,
        textValue: `${format.requested_format.toUpperCase()} (.${format.file_extension})`,
        action: () => downloadAdditionalFormat(format, baseName, onTranscriptionDownloaded),
      })),
    [additionalFormats, baseName, onTranscriptionDownloaded],
  );

  function handleDownloadClick() {
    if (hasFormats) {
      setIsFormatDropdownOpen(open => !open);
      return;
    }
    onDownload?.();
  }

  return {
    formatItems: formatItems as ItemBase[],
    hasFormats,
    isFormatDropdownOpen,
    setIsFormatDropdownOpen,
    handleDownloadClick,
  };
}
